import { useEffect, useRef, useState } from 'react';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import { Auth, Hub } from 'aws-amplify';
import _debounce from 'lodash/debounce';
import { batch, useDispatch } from 'react-redux';

import config from 'config';

import { RedirectAction, UserActionType } from 'types';
import { getCallbackTriggersMap, getUserSession, refreshTokenForStreamingPlatform } from 'utils';
import { authActions, loaderActions, profileActions, sessionActions, streamingPlatformsActions } from 'store/actions';
import {
  authSelector,
  callbackTriggersSelector,
  expiredStreamingPlatformsSelector,
  fingerprintSelector
} from 'store/selectors';
import { loaderSelector } from 'store/selectors/loader';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { api, LoggerService, tracker } from 'services';
import { usePageTracking, useRouter, useStore } from 'hooks';
import { Notifications } from 'containers';
import { Loader } from 'components';

import AppRoutes from './App.Routes';

const App = () => {
  usePageTracking();
  const visitorData = useVisitorData({}, { immediate: false });
  const dispatch = useDispatch();
  const router = useRouter();
  const { auth, fingerprint, loader, expiredStreamingPlatforms, callbackTriggers } = useStore(state => ({
    auth: authSelector(state),
    fingerprint: fingerprintSelector(state),
    loader: loaderSelector(state),
    expiredStreamingPlatforms: expiredStreamingPlatformsSelector(state),
    callbackTriggers: callbackTriggersSelector(state)
  }));
  const fromSignIn = useRef(false);

  const [authorizing, setAuthorizing] = useState(true);

  useEffect(() => {
    document.body.classList.add('mobile-view', 'position-relative');
  }, []);

  // recalculate height for full screen effect
  // SOURCE: https://chanind.github.io/javascript/2019/09/28/avoid-100vh-on-mobile-web.html#comment-4634921967
  useEffect(() => {
    const setDocHeight = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight / 100}px`);
    };

    const delayedSetDocHeight = _debounce(setDocHeight, 200);

    window.addEventListener('resize', delayedSetDocHeight);
    window.addEventListener('orientationchange', delayedSetDocHeight);

    document.documentElement.style.setProperty('--vh', `${window.innerHeight / 100}px`);

    return () => {
      window.removeEventListener('resize', delayedSetDocHeight);
      window.removeEventListener('orientationchange', delayedSetDocHeight);
    };
  }, []);

  useEffect(() => {
    setAuthorizing(true);
    dispatch(loaderActions.showLoader());

    tracker.setVersion(config.app.version);

    Hub.listen('auth', ({ payload: { event, data } }) => {
      const loggedInWithPhoneNumber = !!data?.attributes;

      if (loggedInWithPhoneNumber) {
        const { username, email, phoneNumber, name, image, accessToken } = getUserSession(data);

        dispatch(
          authActions.setAuth({
            username,
            name,
            image,
            email,
            phoneNumber,
            accessToken
          })
        );
      }

      switch (event) {
        case 'signIn': {
          fromSignIn.current = true;

          dispatch(loaderActions.hideLoader());
          break;
        }

        case 'signOut': {
          dispatch(authActions.resetAuth());
          dispatch(profileActions.resetProfile());
          return null;
        }
      }

      return null;
    });

    Auth.currentSession()
      .then(userSession => {
        const accessToken = userSession.getAccessToken().getJwtToken();

        authActions.setAuth({ accessToken });
      })
      .catch(error => {
        if (error !== 'No current user') {
          LoggerService.log('CURRENT_SESSION', error);
        }
      });

    Auth.currentAuthenticatedUser()
      .then(data => {
        const { provider, username, email, phoneNumber, name, image, accessToken } = getUserSession(data);

        // NOTE: This line detects user login. This could be done on Hub.listen but catching profile
        // info inside it was not possible so fromSignIn was used as an indicator
        if (fromSignIn.current) {
          tracker.signIn({ provider, username, email, phoneNumber, name, image });
        }

        tracker.identify(username);
        tracker.setProfileInfo({ provider, username, email, phoneNumber, name, image });

        fromSignIn.current = false;
        dispatch(
          authActions.setAuth({
            username,
            name,
            image,
            email,
            phoneNumber,
            accessToken
          })
        );
      })
      .catch(e => {
        if (e !== 'The user is not authenticated') {
          LoggerService.log('CURRENT_AUTHENTICATED_USER', e);
        }
        dispatch(authActions.resetAuth());
      })
      .finally(() => {
        dispatch(loaderActions.hideLoader());
        setAuthorizing(false);
      });
  }, []);

  useEffect(() => {
    api.requests.youtube.init().then(() => {
      const refreshPromises = expiredStreamingPlatforms.map(expiredPlatform =>
        refreshTokenForStreamingPlatform(expiredPlatform.key)
      );

      Promise.all(refreshPromises).then(tokens => {
        batch(() => {
          tokens.forEach(({ platformKey, token: { accessToken, expiresAt } }) => {
            dispatch(
              streamingPlatformsActions.refreshToken({
                platformKey,
                state: { accessToken, expiresAt }
              })
            );
          });
        });
      });
    });
  }, []);

  useEffect(() => {
    if (auth.isAuth) {
      fetchSubscribedChannels();
      fetchUser();
    }
  }, [auth.isAuth]);

  useEffect(() => {
    if (auth.isAuth && callbackTriggers.length) {
      const callbackTriggersMap = getCallbackTriggersMap(callbackTriggers);
      const redirectTrigger = callbackTriggersMap[UserActionType.REDIRECT] as RedirectAction | undefined;

      if (redirectTrigger) {
        dispatch(callbackTriggersActions.remove(UserActionType.REDIRECT));

        if (redirectTrigger.payload?.path) {
          router.replace(redirectTrigger.payload.path, { ...redirectTrigger.payload?.params });
        }
      }
    }
  }, [auth.isAuth, callbackTriggers]);

  useEffect(() => {
    if (!auth.isAuth && !fingerprint) {
      getUserFingerprint();
    }
  }, [auth.isAuth, fingerprint]);

  const getUserFingerprint = async () => {
    try {
      const data = await visitorData.getData();

      if (data?.visitorId) {
        dispatch(sessionActions.setFingerprint(data.visitorId));
      }
    } catch (e) {
      LoggerService.log('GET_USER_FINGERPRINT', e);
    }
  };

  const fetchSubscribedChannels = () => {
    api.requests.ltv
      .getSubscribedChannels()
      .then(response => {
        dispatch(profileActions.setChannels(response.data));
      })
      .catch(e => {
        LoggerService.log('FETCH_SUBSCRIBED_CHANNELS', e);
      });
  };

  const fetchUser = () => {
    api.requests.ltv
      .getUser()
      .then(response => {
        const channelName = response.data?.channelName;

        dispatch(profileActions.setUser(response.data));

        if (channelName) {
          fetchChannel(channelName);
        }
      })
      .catch(e => {
        LoggerService.log('FETCH_USER', e);
      });
  };

  const fetchChannel = async channelName => {
    const { data: channel } = await api.requests.ltv.getChannelByName(channelName);
    const { membershipPricingPlan } = channel;

    if (membershipPricingPlan) {
      dispatch(profileActions.setMembershipPricingPlan(membershipPricingPlan));
    }
  };

  return (
    <>
      <Notifications />

      {loader.isLoading ? <Loader global /> : null}
      {!authorizing && <AppRoutes />}
    </>
  );
};

export default App;
